/**
 * todo: refactor for loops to enable eslint back
 */
/* eslint-disable no-restricted-syntax */
import { Entries } from 'type-fest';
import { z, ZodFirstPartyTypeKind, ZodTypeAny } from 'zod';

import {
  AnyObject,
  JSONSchema,
  PlatformAppUiSchema,
  SchemaProperties,
} from '@typings';
import {
  as,
  capitalize,
  conditionalProperty,
  isArray,
  isNumber,
  isObject,
  isString,
  joinNonEmptyStrings,
  unflattenObject,
} from '@utils';
import {
  isSchemaAnyOf,
  isSchemaArray,
  isSchemaContainer,
  isSchemaField,
  isSchemaWithChildren,
} from '@utils/guards';

type RequiredSchema = Record<string, boolean>;

type SchemaContext = {
  parent?: string;
  requiredSchema: RequiredSchema;
};

type Schema = Record<string, any>;

type ModelChildren = PlatformAppUiSchema.ModelWithChildren['children'];

type Arguments = {
  schema: Schema;
  root: JSONSchema;
  context: SchemaContext;
  debug?: string;
  /**
   * JSON Schema definition type name
   */
  typeName?: string;
  prefix?: string;
  shouldCheckParentRequired?: boolean;
  schemaDefaultValues?: AnyObject;
};

const extractMetadata = (schema: Schema) => {
  if (!isObject(schema)) {
    return {};
  }

  const {
    title,
    'x-title': xTitle,
    description,
    'x-description': xDescription,
  } = schema as any;

  return {
    ...conditionalProperty('title', xTitle || title),
    ...conditionalProperty('description', xDescription || description),
  };
};

function resolveRef(
  ref: string,
  schema: JSONSchema,
): { type: string; schema: JSONSchema } {
  const type = ref.replace(/^#\/\$defs\//, '');

  return { schema: schema.$defs[type], type };
}

/**
 * External properties are properties with `x-` prefix
 */
const extractExternalProperties = (schema: Schema) => {
  const properties = (
    Object.entries(schema) as Entries<SchemaProperties.Properties>
  ).reduce((properties, [property, value]) => {
    if (property.startsWith('x-')) {
      properties[property] = value;
    }

    return properties;
  }, {} as SchemaProperties.Properties);

  return properties;
};

const extractMetaTypeProperty = (
  properties: SchemaProperties.Properties | Schema,
): SchemaProperties.Properties['x-meta-type'] => properties['x-meta-type'];

/**
 * Get type properties which are located along with $ref
 * to override them in resolved schema
 */
const getInheritedProperties = (schema: Schema) => {
  const properties = extractExternalProperties(schema);

  if ('default' in schema) {
    properties.default = schema.default;
  }

  return properties;
};

const getDefaultValue = (
  name: string,
  defaultValues: AnyObject | null | undefined,
  ...values: unknown[]
): any => {
  if (isObject(defaultValues) && name in defaultValues) {
    return defaultValues[name];
  }

  return values.find((value) => value !== undefined);
};

function extractProperties({
  schema,
  root,
  context,
  prefix = '',
  typeName,
  shouldCheckParentRequired = true,
  schemaDefaultValues,
}: Arguments): PlatformAppUiSchema.Model {
  const metadata = extractMetadata(schema);
  const children: ModelChildren = [];

  const result = {} as PlatformAppUiSchema.Model;

  Object.assign(result, metadata);

  /**
   * `anyOf` `null` node use-case
   * Fill empty `null` node with properties for form
   */
  if (schema.type === 'null') {
    const name = joinNonEmptyStrings(prefix, schema.type);

    Object.assign(result, {
      type: PlatformAppUiSchema.Type.Field,
      name,
      title: 'None',
      typeName,
      properties: {
        title: schema.title,
        description: schema.description,
        type: schema.type,
        name,
        required: false,
      },
    });

    return result;
  }

  /**
   * Currently, it is caught by `anyOf` children only
   */
  if (schema.type && schema.type !== 'object') {
    const name = joinNonEmptyStrings(prefix);
    const required = schema.required?.includes(prefix) ?? false;

    Object.assign(result, {
      ...conditionalProperty(
        'metaType',
        extractMetaTypeProperty(extractExternalProperties(schema)),
      ),
      type: PlatformAppUiSchema.Type.Field,
      name,
      typeName,
      title: schema.title ?? capitalize(schema.type),
      properties: {
        ...extractMetadata(schema),
        const: schema.const,
        default: schema.default,
        type: schema.type,
        items: schema.items,
        name,
        required,
      },
    });

    return result;
  }

  if (schema.$ref) {
    const { type, schema: resolvedSchema } = resolveRef(schema.$ref, root);
    const inheritedProperties = getInheritedProperties(schema);

    return Object.assign(result, {
      ...extractMetadata(resolvedSchema),
      ...extractProperties({
        schema: resolvedSchema,
        root,
        context,
        prefix,
        typeName: type,
      }),
      ...extractMetadata(inheritedProperties),
      ...conditionalProperty(
        'metaType',
        extractMetaTypeProperty({ ...resolvedSchema, ...inheritedProperties }),
      ),
      type: PlatformAppUiSchema.Type.Container,
      typeName: type,
      name: joinNonEmptyStrings(prefix, typeName),
    });
  }

  if (schema.properties) {
    for (const [key, propSchema] of Object.entries<any>(schema.properties)) {
      const fullKeyName = joinNonEmptyStrings(prefix, key);
      const inheritedProperties = getInheritedProperties(propSchema);
      /**
       * Let parent to be required (when top properties are extracting)
       * on initial properties extraction.
       * This keeps nested `required` properties intact
       */
      const isParentRequired = (() => {
        if (!shouldCheckParentRequired) {
          return true;
        }

        if (!prefix) {
          return false;
        }

        return Boolean(context.requiredSchema[prefix]);
      })();
      const required = isParentRequired
        ? schema.required?.includes(key) ?? false
        : false;

      context.requiredSchema[fullKeyName] = required;

      if (propSchema.$ref) {
        const { type, schema: resolvedSchema } = resolveRef(
          propSchema.$ref,
          root,
        );

        const isEnum = isArray(resolvedSchema.enum);

        if (isEnum) {
          const { description, title } = {
            ...extractMetadata(propSchema),
            ...extractMetadata(inheritedProperties),
          };

          children.push({
            ...conditionalProperty(
              'metaType',
              extractMetaTypeProperty({
                ...propSchema,
                ...inheritedProperties,
              }),
            ),
            type: PlatformAppUiSchema.Type.Field,
            typeName: type,
            name: fullKeyName,
            properties: {
              type: 'enum',
              title,
              description,
              name: fullKeyName,
              options: resolvedSchema.enum,
              required,
              default: getDefaultValue(
                key,
                { ...inheritedProperties, ...schemaDefaultValues },
                propSchema.const,
                propSchema.default,
              ),
            },
          });
        } else if (resolvedSchema) {
          children.push({
            ...extractMetadata(resolvedSchema),
            ...extractMetadata(inheritedProperties),
            ...conditionalProperty(
              'metaType',
              extractMetaTypeProperty({
                ...resolvedSchema,
                ...inheritedProperties,
              }),
            ),
            type: PlatformAppUiSchema.Type.Container,
            typeName: type,
            name: fullKeyName,
            children: (
              extractProperties({
                schema: resolvedSchema,
                root,
                context,
                prefix: fullKeyName,
                typeName: type,
                schemaDefaultValues: inheritedProperties.default,
              }) as PlatformAppUiSchema.ModelWithChildren
            ).children,
          });
        }
      } else if (propSchema.anyOf) {
        const anyOfChildren = propSchema.anyOf.map((schema: JSONSchema) =>
          extractProperties({
            schema,
            root,
            context,
            prefix: fullKeyName,
            typeName,
          }),
        );

        children.push({
          type: PlatformAppUiSchema.Type.AnyOf,
          name: fullKeyName,
          ...conditionalProperty(
            'metaType',
            extractMetaTypeProperty({
              ...propSchema,
              ...inheritedProperties,
            }),
          ),
          ...extractMetadata(propSchema),
          typeName,
          children: anyOfChildren,
          ...(propSchema.default !== undefined && {
            default: propSchema.default,
          }),
        });
      } else if (propSchema.type) {
        const isArray = propSchema.type === 'array';
        const isArrayWithReference = isArray && !!propSchema.items.$ref;
        const arrayOf = isArray ? propSchema.items : null;

        if (isArrayWithReference) {
          const { type, schema: resolvedSchema } = resolveRef(
            propSchema.items.$ref,
            root,
          );
          /**
           * To build UI Schemas nested form components,
           * replace `$index` with useFieldArrays indexes
           */
          const arrayName = joinNonEmptyStrings(fullKeyName, '$index');
          const inheritedProperties = getInheritedProperties(propSchema);

          children.push({
            ...propSchema,
            ...extractMetadata(propSchema),
            ...inheritedProperties,
            ...conditionalProperty(
              'metaType',
              extractMetaTypeProperty({
                ...propSchema,
                ...inheritedProperties,
              }),
            ),
            type: PlatformAppUiSchema.Type.Array,
            typeName: type,
            name: fullKeyName,
            children: (
              extractProperties({
                schema: resolvedSchema,
                root,
                context,
                prefix: arrayName,
                typeName: type,
                shouldCheckParentRequired: false,
              }) as PlatformAppUiSchema.ModelWithChildren
            ).children,
          });
        } else {
          children.push({
            type: PlatformAppUiSchema.Type.Field,
            typeName,
            name: fullKeyName,
            properties: {
              ...propSchema,
              ...extractMetadata(propSchema),
              ...conditionalProperty('arrayOf', arrayOf),
              name: fullKeyName,
              default: getDefaultValue(
                key,
                schemaDefaultValues,
                propSchema.const,
                propSchema.default,
              ),
              required,
            },
          });
        }
      }
    }
  }

  if (isSchemaWithChildren(result)) {
    result.children = children;
  }

  return result;
}

function extractChildrenFields(
  children: ModelChildren,
): PlatformAppUiSchema.Field[] {
  return children
    .map((child) => {
      if (isSchemaWithChildren(child)) {
        return extractChildrenFields(child.children);
      }

      if (child.type === PlatformAppUiSchema.Type.Array) {
        return [];
      }

      return child;
    })
    .flat();
}

export function extractSchema(jsonSchema: JSONSchema) {
  if (!jsonSchema.properties) {
    return {
      uiSchemas: [],
      params: {},
    };
  }

  const requiredSchema = as
    .a<string[]>(jsonSchema.required)
    .reduce<RequiredSchema>(
      (schema, field) => ({ ...schema, [field]: true }),
      {},
    );

  const context: SchemaContext = {
    requiredSchema,
  };

  const uiSchemas = (
    extractProperties({
      schema: jsonSchema,
      root: jsonSchema,
      context,
      shouldCheckParentRequired: false,
    }) as PlatformAppUiSchema.ModelWithChildren
  ).children;

  const params = uiSchemas.reduce(
    (params, schema) => {
      if (!isSchemaWithChildren(schema)) {
        return params;
      }

      const fields = extractChildrenFields(schema.children).reduce(
        (fields, { properties }) => ({
          ...fields,
          [properties.name]: properties,
        }),
        {},
      );

      return { ...params, ...fields };
    },
    {} as Record<string, PlatformAppUiSchema.FieldProperties>,
  );

  return { uiSchemas, params };
}

function sanitizePath(name: string): string[] {
  return name.replace(/\$index\./g, '').split('.');
}

function setDeepSchema(
  shape: Record<string, any>,
  path: string[],
  schema: ZodTypeAny,
) {
  const key = path[0];

  if (path.length === 1) {
    shape[key] = schema;

    return;
  }

  if (!shape[key] || !(shape[key] instanceof z.ZodObject)) {
    shape[key] = z.object({});
  }

  const inner = (shape[key] as z.ZodObject<any>).shape;
  const innerShape = { ...inner };

  setDeepSchema(innerShape, path.slice(1), schema);
  shape[key] = z.object(innerShape);
}

function stripNamePrefixDeep<T extends PlatformAppUiSchema.Model>(
  models: T[],
  prefix: string,
): T[] {
  return models.map((model) => {
    const clone = { ...model } as T;

    if ('name' in clone && isString(clone.name)) {
      clone.name = clone.name.replace(prefix, '');
    }

    if (isSchemaField(clone)) {
      clone.properties = {
        ...clone.properties,
        name: clone.properties.name.replace(prefix, ''),
      };
    }

    if (isSchemaWithChildren(clone)) {
      clone.children = stripNamePrefixDeep(clone.children, prefix);
    }

    return clone;
  });
}

function buildPrimitiveSchema(
  props: PlatformAppUiSchema.FieldProperties,
): ZodTypeAny {
  const { type, required, default: def, const: constVal, options } = props;

  let schema: ZodTypeAny;

  switch (type) {
    case 'string':
      schema = required ? z.string().min(1, 'Required') : z.string();
      break;
    case 'number':
    case 'float':
    case 'integer':
      schema = required
        ? z.number().refine(isNumber, { message: 'Required' })
        : z.number();
      break;
    case 'boolean':
      schema = z.boolean();
      break;
    case 'enum':
      if (options && options.length > 0) {
        schema = z.enum([...options] as [string, ...string[]]);
      } else {
        schema = z.string();
      }
      break;
    case 'null':
      schema = z.null();
      break;
    default:
      schema = z.unknown();
  }

  if (!required) {
    schema = schema.optional();
  }

  if (constVal !== undefined) {
    schema = schema.default(constVal);
  } else if (def !== undefined && def !== null) {
    schema = schema.default(def);
  }

  return schema;
}

export function buildSchemaShape(
  models: PlatformAppUiSchema.Model[],
): Record<string, ZodTypeAny> {
  const shape: Record<string, ZodTypeAny> = {};

  for (const model of models) {
    if (isSchemaField(model)) {
      const path = sanitizePath(model.properties.name);

      const schema = buildPrimitiveSchema(model.properties);
      setDeepSchema(shape, path, schema);
    }

    if (isSchemaArray(model)) {
      const path = sanitizePath(model.name);

      const prefix = `${model.name}.$index.`;

      const strippedChildren = model.children.map((child) => {
        if (isSchemaField(child)) {
          return {
            ...child,
            properties: {
              ...child.properties,
              name: child.properties.name.replace(prefix, ''),
            },
          };
        }

        return {
          ...child,
          name: child.name.replace(prefix, ''),
        };
      });

      const childShape = buildSchemaShape(strippedChildren);
      const itemSchema = z.object(childShape);
      const schema = z.array(itemSchema);

      setDeepSchema(shape, path, schema);
    }

    if (isSchemaAnyOf(model)) {
      const path = sanitizePath(model.name);
      const prefix = `${model.name}.`;

      const strippedChildren = stripNamePrefixDeep(model.children, prefix);

      const objectSchemas: ZodTypeAny[] = [];
      const primitiveSchemas: ZodTypeAny[] = [];
      const nullSchemas: ZodTypeAny[] = [];

      strippedChildren.forEach((child) => {
        if (isSchemaField(child) && child.properties.type === 'null') {
          nullSchemas.push(z.null());
        } else if (isSchemaField(child)) {
          primitiveSchemas.push(buildPrimitiveSchema(child.properties));
        } else {
          const nested = z.object(buildSchemaShape([child]));
          objectSchemas.push(nested);
        }
      });

      type UnionType = [ZodTypeAny, ZodTypeAny, ...ZodTypeAny[]];
      const union = z.union([
        ...objectSchemas,
        ...primitiveSchemas,
        ...nullSchemas,
      ] as UnionType);
      const defaultValue = model.default ?? undefined;

      setDeepSchema(
        shape,
        path,
        defaultValue !== undefined ? union.default(defaultValue) : union,
      );
    }

    if (isSchemaContainer(model)) {
      const nestedShape = buildSchemaShape(model.children);

      Object.entries(nestedShape).forEach(([nestedKey, schema]) => {
        const path = sanitizePath(nestedKey);
        setDeepSchema(shape, path, schema);
      });
    }
  }

  return shape;
}

/**
 * Debug utility
 */
function isZodType(schema: any): schema is ZodTypeAny {
  return (
    schema &&
    typeof schema === 'object' &&
    '_def' in schema &&
    /**
     * `_def` is zod property
     */
    // eslint-disable-next-line no-underscore-dangle
    typeof schema._def === 'object'
  );
}

/**
 * Debug utility
 */
/* eslint-disable */
function walkZod(
  schema: unknown,
  path: string[] = [],
  result: Record<string, string> = {},
): Record<string, string> {
  if (!isZodType(schema)) {
    result[path.join('.')] = 'Not a Zod schema';
    return result;
  }

  const { typeName } = schema._def;

  switch (typeName) {
    case ZodFirstPartyTypeKind.ZodObject: {
      const shape = schema._def.shape();
      for (const key in shape) {
        walkZod(shape[key], [...path, key], result);
      }
      break;
    }

    case ZodFirstPartyTypeKind.ZodOptional:
    case ZodFirstPartyTypeKind.ZodNullable:
      walkZod(schema._def.innerType, path, result);
      break;

    case ZodFirstPartyTypeKind.ZodUnion:
      schema._def.options.forEach((option: ZodTypeAny, index: number) => {
        walkZod(option, [...path, `option${index}`], result);
      });
      break;

    default:
      result[path.join('.')] = typeName;
      break;
  }

  return result;
}
/* eslint-enable */

export function makeAppSchema(uiSchemas: PlatformAppUiSchema.Model[]) {
  const schemaShape = buildSchemaShape(uiSchemas);

  return z.object({
    ...schemaShape,
    displayName: z.string(),
  });
}

export const getSchemaDefaultValues = (
  uiSchemas: PlatformAppUiSchema.Model[],
) => {
  type DefaultValueModel = { name: string; default: any };
  const extractNodeDefaultValue = (
    node: PlatformAppUiSchema.Model,
  ): DefaultValueModel[] => {
    if (isSchemaField(node)) {
      return [{ name: node.name, default: node.properties.default }];
    }

    if (isSchemaArray(node) || isSchemaAnyOf(node)) {
      return [{ name: node.name, default: node.default }];
    }

    const containerDefaultValues = node.children.map(extractNodeDefaultValue);

    return containerDefaultValues.flat(10);
  };

  const values = uiSchemas.reduce(
    (values, node) => {
      const nodeDefaultValues = extractNodeDefaultValue(node);

      nodeDefaultValues.forEach(({ name, default: defaultValue }) => {
        if (defaultValue !== undefined) {
          values[name] = defaultValue;
        }
      });

      return values;
    },
    {} as Record<string, any>,
  );

  return unflattenObject(values);
};

export function extractAppParamsSchema(
  params: Record<string, any>,
  root: JSONSchema,
  schema: JSONSchema,
  prefix = '',
): void {
  if (!schema || typeof schema !== 'object') return;

  if (schema.$ref) {
    const { schema: resolved } = resolveRef(schema.$ref, root);
    extractAppParamsSchema(params, root, resolved, prefix);
    return;
  }

  if (Array.isArray(schema.anyOf)) {
    const refEntry = schema.anyOf.find((entry) => '$ref' in entry) as
      | JSONSchema
      | undefined;
    if (refEntry?.$ref) {
      const { schema: resolved } = resolveRef(refEntry.$ref, root);

      extractAppParamsSchema(params, root, resolved, prefix);

      return;
    }
  }

  const meta = extractMetadata(schema);

  if (Object.keys(meta).length > 0 || prefix) {
    params[prefix] = meta;
  }

  if (schema.properties) {
    Object.entries(schema.properties).forEach(([key, value]) => {
      extractAppParamsSchema(
        params,
        root,
        value,
        joinNonEmptyStrings(prefix, key),
      );
    });
  }

  if (schema.type === 'array' && isObject(schema.items)) {
    extractAppParamsSchema(
      params,
      root,
      schema.items as JSONSchema,
      joinNonEmptyStrings(prefix, '[]'),
    );
  }
}

export const makeAppParamsSchema = (schema: JSONSchema) => {
  const params = {};

  extractAppParamsSchema(params, schema, schema);

  return params;
};

export const extractIngerationTypesFromSchema = (
  schema: JSONSchema,
): Record<string, PlatformAppUiSchema.Model> => {
  const { uiSchemas } = extractSchema(schema);
  const result: Record<string, PlatformAppUiSchema.Model> = {};

  const traverse = (schemas: PlatformAppUiSchema.Model[]): void => {
    schemas.forEach((schema) => {
      if (schema.metaType === 'integration') {
        result[schema.name] = schema;
      }

      if (
        isSchemaContainer(schema) ||
        isSchemaAnyOf(schema) ||
        isSchemaArray(schema)
      ) {
        traverse(schema.children);
      }
    });
  };

  traverse(uiSchemas);

  return result;
};
